import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { CATEGORY_BY_TYPE, itemPath } from "@/lib/categories";
import BookmarkButton from "@/components/BookmarkButton";
import { resolveImage, onImgError } from "@/lib/images";
import CardImage from "@/components/CardImage";
import { ArrowRight, Clock, Volume2 } from "lucide-react";

const langLabel = (codes = []) => codes.map((c) => (c === "hi" ? "हिन्दी" : c === "sa" ? "संस्कृत" : "English")).join(" · ");

export default function KnowledgeCard({ item, showImage = false }) {
  const { t, lang, pick, pickTitle, pickSecondary, pickDesc } = useLang();
  const cat = CATEGORY_BY_TYPE[item.type];
  const to = itemPath(item);
  const title = pickTitle(item);
  const secondaryTitle = pickSecondary(item);

  return (
    <Link
      to={to}
      data-testid={`knowledge-card-${item.slug}`}
      className="group flex h-full flex-col rounded-xl border border-[var(--gold)]/25 bg-surface p-6 sm:p-7 shadow-[0_6px_24px_rgba(44,38,33,0.05)] transition-all duration-300 hover:-translate-y-1 hover:border-[var(--gold)]/50 hover:shadow-[0_16px_44px_rgba(199,80,0,0.13)]"
    >
      {showImage && (
        <CardImage
          src={resolveImage(item)}
          alt={title}
          onError={onImgError(item)}
          ratio="aspect-[16/10]"
          rounded="rounded-lg"
          className="mb-5"
        />
      )}
      <div className="mb-3 flex items-start justify-between">
        <span className="inline-flex items-center gap-2">
          <span className="overline-label text-saffron">{pick(cat?.deva, cat?.en) || item.type}</span>
          {item.audio_url && (
            <span data-testid={`card-audio-badge-${item.slug}`} title={t("listen")}
              className="inline-flex items-center gap-1 rounded-full bg-[var(--saffron)]/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--saffron-dark)]">
              <Volume2 className="h-3 w-3" /> {t("listen")}
            </span>
          )}
        </span>
        <BookmarkButton slug={item.slug} title={title} variant="icon" />
      </div>
      <h3 className={`${lang === "en" ? "font-serif-latin text-xl sm:text-2xl" : "font-deva text-2xl sm:text-3xl"} leading-snug text-ink deva-lh`}>{title}</h3>
      {secondaryTitle && (
        <p className={lang === "en"
          ? "mt-1.5 font-deva deva-lh text-lg text-ink-2"
          : "mt-1.5 font-serif-latin text-sm italic text-ink-3"}>{secondaryTitle}</p>
      )}
      <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-2 line-clamp-3">{pickDesc(item)}</p>
      <div className="mt-6 h-px w-full bg-[var(--gold)]/20" />
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-ink-3">
          {item.read_time && (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> {item.read_time}
            </span>
          )}
          {item.languages?.length > 0 && <span>{langLabel(item.languages)}</span>}
        </div>
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-saffron transition-transform group-hover:translate-x-1">
          {t("read")} <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
}
